/**
 * RoleHierarchyTree
 *
 * SCRUM-1806 AC-07/08: visualizes the role implication tree so super admins
 * understand which roles cascade into which. Reads the hierarchy returned by
 * useRoleHierarchy (server source of truth) and renders a nested list with
 * the explanation text that AC-08 requires.
 */

import { useMemo } from 'react';

function buildChildren(hierarchy) {
  // For each role, list the roles that DIRECTLY imply it (parents).
  // Root roles are those with no parents.
  const parents = {};
  for (const [name, def] of Object.entries(hierarchy.roles)) {
    for (const child of def.implies) {
      if (!parents[child]) { parents[child] = []; }
      parents[child].push(name);
    }
  }
  // Children map: role -> direct children (the roles in its implies list)
  const children = {};
  for (const [name, def] of Object.entries(hierarchy.roles)) {
    // Filter implied roles to direct children only — those whose only path
    // from this role is direct (not via another implies). Since the config
    // only lists direct + transitive together, we approximate "direct" as
    // any role NOT implied by another role in the same implies list.
    const direct = def.implies.filter(impliedRole => {
      return !def.implies.some(other => {
        if (other === impliedRole) { return false; }
        const otherImplies = hierarchy.roles[other]?.implies || [];
        return otherImplies.includes(impliedRole);
      });
    });
    children[name] = direct;
  }
  const roots = Object.keys(hierarchy.roles).filter(name => !parents[name]);
  return { children, roots };
}

function RoleNode({ name, childMap, depth }) {
  const childList = childMap[name] || [];
  return (
    <li className="text-sm">
      <span className="font-mono text-gray-900">{name}</span>
      {childList.length > 0 && (
        <ul className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-4">
          {childList.map(child => (
            <RoleNode key={child} name={child} childMap={childMap} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function RoleHierarchyTree({ hierarchy }) {
  const { children: childMap, roots } = useMemo(() => {
    if (!hierarchy) { return { children: {}, roots: [] }; }
    return buildChildren(hierarchy);
  }, [hierarchy]);

  if (!hierarchy) {
    return <p className="text-sm text-gray-500">Loading hierarchy…</p>;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
      <h3 className="text-sm font-semibold text-blue-900 mb-2">Role hierarchy</h3>
      <p className="text-xs text-blue-800 mb-3">
        Granting a higher role automatically grants all roles below it. You can
        remove individual roles afterward to override the cascade.
      </p>
      <ul className="space-y-1">
        {roots.map(root => (
          <RoleNode key={root} name={root} childMap={childMap} depth={0} />
        ))}
      </ul>
    </div>
  );
}
